import { ReactElement, useState } from "react";

import DOMPurify from "dompurify";

import Link from "next/link";

import {
  Anchor,
  AnchorProps,
  Button,
  Input,
  PasswordInput,
} from "@newsela/angelou";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { useForm, SubmitHandler } from "react-hook-form";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";

import globalToasterRef from "@/base/globalToasterRef";
import TermsAndPrivacyPolicy from "@/components/TermsAndPrivacyPolicy";
import identifyMarketoLead from "@/gtmIntegration/identifyMarketoLead/identifyMarketoLead";
import LogInProviders from "@/login/LogInProviders";
import useSessionPost from "@/login/useSessionPost";
import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import {
  appendTrackingParamsToUrl,
  getRedirectQueryString,
} from "@/utils/appendTrackingParamsToUrl";
import { getSafeRedirectDestination } from "@/utils/getSafeRedirectDestination";
import { useGetUser, useRefreshMe } from "@/users/v3";
import {
  EVENT_TOAST_ERROR,
  ACCOUNT_LOCKED_ERROR,
  LOGIN_METHOD_NOT_ALLOWED,
} from "./LogInPage.utils";

import {
  containerCss,
  errorMessageCss,
  joinNowCss,
  resetPasswordAnchorCss,
  signInButtonCss,
  titleCss,
  centralizedContainerCss,
  notMemberCss,
  signInFormCss,
  dividerCss,
  signInContainerCss,
  usernameFieldCss,
  passwordFieldCss,
  alreadySignedInContainerCss,
} from "./LogInPage.style";

export interface FormValues {
  username: string;
  password: string;
}

const ButtonWithTrackEvent = withTrackEvent(Button);
const AnchorWithTrackEvent = withTrackEvent<AnchorProps>(Anchor);

const buildSignUpHref = (query: ReturnType<typeof useRouter>["query"]) => {
  const queryString = getRedirectQueryString(query);

  return `${MONOLITH_ORIGIN}/join/${queryString ? `?${queryString}` : ""}`;
};

const LogInPage = (): ReactElement => {
  const { data: user } = useGetUser();
  const refreshMe = useRefreshMe();
  const router = useRouter();
  const { query } = router;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();
  const [createSession] = useSessionPost();
  const [loading, setLoading] = useState(false);
  const redirectAfterSetup = query["redirect_after_setup"];
  const [showAlreadyLoggedMessage, setShowAlreadyLoggedMessage] =
    useState(true);
  const signUpHref = buildSignUpHref(query);

  const onSignIn: SubmitHandler<FormValues> = async (form) => {
    setLoading(true);
    const response = await createSession(form);

    if (response.error) {
      let errorMessage;

      try {
        const { error } = JSON.parse(response.error.toJSON().message);
        errorMessage = error;
      } catch (e) {
        console.error(e);
      }

      if (response.error.statusCode === 403) {
        if (errorMessage.startsWith(LOGIN_METHOD_NOT_ALLOWED)) {
          const validProviders = errorMessage
            .split(":")
            .pop()
            .trim()
            .replace(".", "");
          window.location.assign(
            `${MONOLITH_ORIGIN}/social-auth-error/?error-type=blocked-auth&provider=password&valid-providers=${validProviders}`,
          );
          return;
        }

        if (errorMessage !== ACCOUNT_LOCKED_ERROR) {
          window.location.assign(`${MONOLITH_ORIGIN}/account-problem`);
          return;
        }
      }

      if (errorMessage) {
        const message = DOMPurify.sanitize(errorMessage, {
          ALLOWED_TAGS: ["a"],
          ALLOWED_ATTR: ["href", "rel"],
        });

        globalToasterRef.current.displayToast({
          ...EVENT_TOAST_ERROR,
          children: (
            <span
              css={errorMessageCss}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: message,
              }}
            />
          ),
        });
      } else {
        globalToasterRef.current.displayToast(EVENT_TOAST_ERROR);
      }

      setLoading(false);
    } else {
      try {
        setShowAlreadyLoggedMessage(false);
        const reloadedUser = await refreshMe();
        await identifyMarketoLead(reloadedUser);
        const destination = getSafeRedirectDestination(
          query["next"],
          MONOLITH_ORIGIN,
        );
        window.location.href = appendTrackingParamsToUrl(destination, query);
      } catch (e) {
        globalToasterRef.current.displayToast({
          ...EVENT_TOAST_ERROR,
          children: "Please, try again. An unexpected error happened",
        });
        console.error(e);
        setLoading(false);
      }
    }
  };

  if (redirectAfterSetup) {
    Cookies.set("redirect_after_setup", redirectAfterSetup.toString(), {
      path: "/",
      secure: true,
      sameSite: "Strict",
      expires: 1 / 24,
    });
  }

  if (user?.is_authenticated && showAlreadyLoggedMessage) {
    return (
      <div css={alreadySignedInContainerCss}>
        <div>
          You are already signed in as <em>{user.email}</em>.
        </div>
        <div>
          <Anchor href="/">Go back to the home page</Anchor>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div css={containerCss}>
        <LogInProviders />
        <hr css={dividerCss} />
        <div css={signInContainerCss}>
          <h1 css={titleCss}>Sign in through Newsela</h1>
          <form
            css={signInFormCss}
            onSubmit={(event) => {
              void handleSubmit(onSignIn)(event);
            }}
            autoComplete="off"
          >
            <Input
              label="Username"
              inputType={Input.inputTypes.text}
              error={errors.username ? "Please enter your username." : ""}
              autocomplete="username"
              touched
              __cssFor={{ root: usernameFieldCss }}
              {...register("username", { required: true })}
              underInputId="email-input-sign-in"
            />
            <PasswordInput
              label="Password"
              error={
                errors.password
                  ? "Your password should be 6 characters or more"
                  : ""
              }
              touched
              __cssFor={{ root: passwordFieldCss }}
              autocomplete="current-password"
              underInputId="password-input-sign-in"
              {...register("password", { required: true, minLength: 6 })}
            />
            <ButtonWithTrackEvent
              dataProps={{
                "data-qa-selector": "sign_in",
                "data-testid": "sign_in_button",
              }}
              objectType={objectTypes.user}
              actionName={actionNames.select}
              streamProperties={{
                description:
                  "Select the button allowing users to sign into Newsela.",
              }}
              flavor={Button.flavors.primary}
              __cssFor={signInButtonCss}
              isFullWidth
              type="submit"
              disabled={loading}
            >
              Sign in
            </ButtonWithTrackEvent>
          </form>
          <div css={centralizedContainerCss}>
            <Link href="/signin/password-reset/" passHref legacyBehavior>
              <AnchorWithTrackEvent
                objectType={objectTypes.user}
                actionName={actionNames.select}
                streamProperties={{
                  description:
                    "Select the button allowing users to reset their passwords if they've forgotten them.",
                }}
                dataProps={{
                  "data-qa-selector": "forgot_password",
                }}
                __cssFor={resetPasswordAnchorCss}
                styleAnchorAsButton={{
                  isFullWidth: true,
                  flavor: Button.flavors.tertiary,
                }}
              >
                Forgot password
              </AnchorWithTrackEvent>
            </Link>
            <div>
              <span css={notMemberCss}>Not a member yet?</span>{" "}
              <AnchorWithTrackEvent
                objectType={objectTypes.user}
                actionName={actionNames.select}
                dataProps={{
                  "data-qa-selector": "sign_up",
                }}
                streamProperties={{
                  description:
                    "Select the button allowing users to sign up for a Newsela account.",
                }}
                href={signUpHref}
                __cssFor={joinNowCss}
              >
                Sign up
              </AnchorWithTrackEvent>
            </div>
          </div>
        </div>
      </div>
      <TermsAndPrivacyPolicy />
    </div>
  );
};

export default LogInPage;
